import React from "react";
import { PACKAGES, ART_DISCLAIMER, mapUrl } from "../shared/catalog.js";
import { deadlineLabel } from "../shared/season.js";
import {
  SectionIntro,
  PackageCard,
  ArrowRight,
  Star,
  Gift,
  Mail,
  Truck,
  Heart,
  TreePine,
} from "./components.jsx";

const FAQ = [
  [
    "How does the Christmas surprise work?",
    "Choose a family package and order online. Pay online or at drop-off, and bring your children’s letters to a collection point. We’ll put together your package and quietly deliver it December 6–12.",
  ],
  [
    "Do we have to write a letter to Santa?",
    "No. Your family can still enjoy a package without dropping off letters. We’ll include a generic Santa letter for your family. If you submit letters, we can reply to the children personally.",
  ],
  [
    "What should we write on the envelope?",
    "Write your delivery address and order number on the outside of each letter’s envelope. Your order number is on your confirmation email and order page.",
  ],
  [
    "Do you take card or cash?",
    "Yes, we take both. Choose your payment at checkout. To pay by card, choose Pay online and finish on Stripe’s secure checkout page. To pay cash, place it in a sealed envelope labeled with your order number, your name and delivery address, and drop it off with your letters at a collection point by December 4. All sales are final. We’ll always try to work with you, but refunds and changes aren’t guaranteed.",
  ],
  [
    "What if we drop off a letter without ordering?",
    "We’d never want a letter to Santa to go unanswered. Letters without a paid order are forwarded to the Idaho Christmas letter program so your child can still receive a response. They aren’t treated as an order—just a little favor so every wish gets a reply.",
  ],
  [
    "When is the deadline?",
    "Orders, payment and letters are due December 4. We’ll collect the letters after the deadline and deliver family packages December 6–12.",
  ],
  [
    "Where do you deliver?",
    "We deliver in Idaho Falls, Ammon, Shelley and Rigby. Delivery is included. Add gate codes, apartment details and a sheltered drop-off spot at checkout.",
  ],
  [
    "How many children can be included?",
    `${PACKAGES.filter((p) => p.kind !== "letter")
      .map((p) => `${p.name} includes ${p.includedChildren} children`)
      .join(", ")}. Additional children are $2 each, including a letter and extra physical items. A Letter from Santa on its own is $5 per child. Tell us each child’s first name and age when customizing your order.`,
  ],
  [
    "Can I change or cancel my order?",
    "Orders can’t be changed or canceled on the website, but just email us at whimsydelivered@gmail.com with your order number. We’ll do our best to work with you to fix a detail, modify your order or sort out anything else.",
  ],
];

export function Home({ config, onChoose }) {
  const deadline = deadlineLabel(config);
  return (
    <>
      <section className="hero">
        <div className="hero-copy">
          <div className="hero-eyebrow">
            <span /> LITTLE SURPRISES. LASTING MEMORIES.
          </div>
          <span className="hero-script">A little Christmas</span>
          <h1>
            whimsy,
            <br />
            <em>delivered.</em>
          </h1>
          <p>
            A doorstep surprise. A letter from Santa.
            <br />A little more wonder for the ones you love.
          </p>
          <div className="hero-actions">
            <a className="button" href="#packages">
              Find your Christmas magic <ArrowRight size={17} />
            </a>
            <a className="text-link" href="#how-it-works">
              How it works <span>↗</span>
            </a>
          </div>
          <div className="hero-note">
            <Heart size={15} />
            <span>Dreamed up by two local moms. Delivered with love.</span>
          </div>
        </div>
        <div className="hero-visual">
          <img
            className="hero-image"
            src="/images/christmas-doorstep.png"
            alt="Christmas concept illustration of a gift-filled basket on a warmly lit snowy doorstep"
            fetchPriority="high"
          />
          <span className="hero-art-label">
            {ART_DISCLAIMER}
          </span>
          <div className="hero-seal">
            <Star size={17} />
            <span>
              FROM OUR
              <br />
              FAMILIES
              <br />
              <em>to yours</em>
            </span>
          </div>
        </div>
        <span className="hero-sparkle sparkle-one">✧</span>
        <span className="hero-sparkle sparkle-two">✦</span>
      </section>
      <div className="service-strip">
        <span>
          <Truck size={17} /> A little North Pole delivery, right here at home.
        </span>
        <span className="towns">
          IDAHO FALLS <i>✦</i> AMMON <i>✦</i> SHELLEY <i>✦</i> RIGBY
        </span>
      </div>
      <section id="how-it-works" className="section how-section">
        <SectionIntro
          eyebrow="A LITTLE PLANNING. A LOT OF MAGIC."
          title="We’ll take it from here."
        >
          Three simple steps to a Christmas memory they’ll keep.
        </SectionIntro>
        <div className="steps">
          {[
            [
              Gift,
              "01",
              "Choose your magic",
              "Pick the family package that feels just right. Each package includes 2, 4 or 6 children, with a little extra magic for each additional child.",
            ],
            [
              Mail,
              "02",
              "Send a little wish",
              `Order online, then drop your letters at a local collection point by ${deadline}. Pay online or at drop-off.`,
            ],
            [
              Truck,
              "03",
              "Watch for the wonder",
              "Our elves quietly deliver December 6–12. A reply from Santa and a basket of together-time arrive at your doorstep.",
            ],
          ].map(([Icon, n, title, body]) => (
            <article className="step" key={n}>
              <span className="step-icon">
                <Icon size={30} strokeWidth={1.3} />
                <span>{n}</span>
              </span>
              <h3>{title}</h3>
              <p>{body}</p>
            </article>
          ))}
        </div>
      </section>
      <section id="packages" className="section packages-section">
        <SectionIntro
          eyebrow="PICK YOUR PERFECT LITTLE SURPRISE"
          title="Good things come in baskets."
        >
          A little something for the kids. A whole lot of Christmas for the
          family.
        </SectionIntro>
        <div className="package-grid">
          {PACKAGES.map((p, i) => (
            <PackageCard
              pack={p}
              index={i}
              key={p.id}
              onChoose={onChoose}
              disabled={config && !config.acceptingOrders}
            />
          ))}
        </div>
        <p className="fine-print">
          Delivery included in our four local towns.
        </p>
      </section>
      <section id="letters" className="letter-section">
        <div className="letter-art">
          <div className="letter-paper">
            <TreePine size={28} strokeWidth={1.1} />
            <span className="letter-script">Dear Santa,</span>
            <div className="letter-lines" />
            <span className="letter-love">With love & a little wonder</span>
          </div>
          <div className="envelope">
            <svg viewBox="0 0 260 162" preserveAspectRatio="none" aria-hidden="true">
              <rect width="260" height="162" fill="#ece2cc" stroke="#daceb3" />
              <path d="M0 0 L120 86 L0 162 Z" fill="#e7dcc2" />
              <path d="M260 0 L140 86 L260 162 Z" fill="#e7dcc2" />
              <path d="M0 162 L130 78 L260 162 Z" fill="#eee5d2" stroke="#d6c8ac" />
              <path d="M0 0 L130 92 L260 0 Z" fill="#f2ead9" stroke="#d6c8ac" />
            </svg>
            <span className="wax-seal">
              <Star size={21} />
            </span>
          </div>
          <span className="letter-star">✧</span>
        </div>
        <div className="letter-copy">
          <span className="eyebrow">SPECIAL DELIVERY TO THE NORTH POLE</span>
          <h2>
            Little letters.
            <br />
            <em>Big Christmas wishes.</em>
          </h2>
          <p>
            There’s something magical about a handwritten wish. Drop off your
            children’s letters, and we’ll help a little Christmas magic find its
            way back home.
          </p>
          <p className="letter-instructions">
            Write your delivery address and order number on each envelope. No
            letter? Your package will include a Santa letter for the whole
            family.
          </p>
          <div className="deadline-note">
            <Mail size={21} />
            <span>
              <strong>Letters & orders close {deadline}</strong>
              <br />
              Delivery December 6–12 · No appointment needed
            </span>
          </div>
          <div className="collection-points">
            {config?.locations?.length ? (
              <>
                <h3>Your local collection points</h3>
                {config.locations.map((l, i) => (
                  <div className="collection-item" key={l.name}>
                    <strong>
                      Collection point {i + 1} · {l.name}
                    </strong>
                    <a href={mapUrl(l)} target="_blank" rel="noreferrer">
                      {l.address} <span>↗</span>
                    </a>
                    <small>{l.hours}</small>
                  </div>
                ))}
                <p className="collection-more">More locations coming soon.</p>
              </>
            ) : (
              <p>
                <strong>Collection points are coming soon.</strong>
                <br />
                We’re finalizing local locations. Check here before dropping off
                letters.
              </p>
            )}
          </div>
        </div>
      </section>
      <section id="about" className="section about-section">
        <div className="founder-photos">
          <figure className="polaroid maddy">
            <img
              src="/images/family-maddy.jpg"
              alt="Maddy with her family"
              loading="lazy"
            />
            <figcaption>Maddy & family</figcaption>
          </figure>
          <figure className="polaroid mackenzie">
            <img
              src="/images/family-mackenzie.jpg"
              alt="Mackenzie with her family"
              loading="lazy"
            />
            <figcaption>Mackenzie & family</figcaption>
          </figure>
        </div>
        <div className="about-copy">
          <span className="eyebrow">
            A LITTLE ABOUT THE MOMS BEHIND THE MAGIC
          </span>
          <h2>
            From our families,
            <br />
            <em>to yours.</em>
          </h2>
          <p>
            We’re Maddy and Mackenzie—two local moms helping
            make Christmas a little more magical.
          </p>
          <p>
            Whimsy Delivered brings together the things we love about the
            season: thoughtful surprises, letters to Santa, and little moments
            that become core family memories.
          </p>
          <p>
            We’ll handle the basket and the doorstep surprise. You get to enjoy
            the look on their faces.
          </p>
          <span className="founder-signature">
            Maddy & Mackenzie <Heart size={18} />
          </span>
        </div>
      </section>
      <section id="questions" className="section faq-section">
        <SectionIntro
          eyebrow="A FEW THINGS YOU MIGHT BE WONDERING"
          title="Before the elves get to work…"
        />
        <div className="faq-list">
          {FAQ.map(([q, a]) => (
            <details key={q}>
              <summary>
                {q}
                <span>+</span>
              </summary>
              <p>{a.replaceAll("December 4", deadline)}</p>
            </details>
          ))}
        </div>
      </section>
      <section className="closing-banner">
        <span>✧</span>
        <div>
          <p className="eyebrow">THE BEST PART IS THE MEMORY.</p>
          <h2>A little magic goes a long way.</h2>
        </div>
        <a className="button button-cream" href="#packages">
          Choose your family’s surprise <ArrowRight size={17} />
        </a>
      </section>
    </>
  );
}
